import { timelockDecrypt } from 'ts-ibe';

/**
 * Decrypts IBE-encrypted randomness under an explicit identity.
 *
 * Identity is the string the ciphertext was encrypted to — typically
 * `address.toLowerCase() + chainPk`, stored alongside the ciphertext as
 * `encrypted*RandomnessId` / `queuedEncrypted*RandomnessId`.
 *
 * @param {string} identity - IBE identity for this ciphertext
 * @param {string} encryptedRandomness - Base64-encoded encrypted randomness
 * @param {string} ibePrivateKey - IBE private key for that identity
 * @returns {Promise<string>} Base64-encoded decrypted randomness (PedersenOpening)
 */
export const decryptRandomness = async (identity, encryptedRandomness, ibePrivateKey) => {
  if (!ibePrivateKey) {
    throw new Error('IBE private key is required for decryption');
  }
  if (identity == null || typeof identity !== 'string' || !identity.trim()) {
    throw new Error('IBE identity is required for decryption');
  }
  if (!encryptedRandomness) {
    throw new Error('Encrypted randomness is required for decryption');
  }

  const id = identity.toLowerCase().trim();

  // Decrypt using IBE — timelockDecrypt returns a Promise<Buffer>
  const decryptedBuffer = await timelockDecrypt(id, ibePrivateKey, encryptedRandomness);

  return decryptedBuffer.toString('base64');
};
